import { getFirebase } from 'firestorter';

import { ActionItemStatus, ActionItemType } from '../Model/UserActionItem';
import { LeadHistoryType } from '../Model/Lead';
import CollectionName from '../../utils/collections';

/**
 * Send a lead message via SendBlue through the server.
 *
 * @param {Object} sendData - The data to send, including leadId, messageBody, msgProvider and isLeadChat.
 * @param {firebase.remote.RemoteType} remote - The Firebase remote object for Firebase-related operations.
 * @returns {Promise<boolean>} Resolves with a boolean indicating whether the operation was successful.
 */
const sendLeadMessage = async (sendData, remote) => {
  const response = await remote('lead-sendLeadMessage', sendData);

  if (!response.ok) {
    /*
     * The function reports why a send failed in its JSON body (`{ success: false, error }`) — for a
     * text that now carries SendBlue's own rejection reason. Discarding it left the agent with a
     * generic message and no way to report what went wrong. The generic text stays as the fallback,
     * so a response that isn't JSON (a gateway error page, say) can't mask the failure itself.
     */
    let serverError;
    try {
      ({ error: serverError } = await response.json());
    } catch {
      serverError = undefined;
    }

    throw new Error(serverError || `Error occurred while sending the message via ${sendData.msgProvider}`);
  }

  return response.ok;
};

// Comment logged when a lead is handed back from a human SDR to aiSDR.
const AI_SDR_RE_ENABLED_COMMENT = 'AI SDR re-enabled';

/**
 * Add a comment to a lead conversation and unarchive the conversation.
 *
 * @param {Object} commentData - The data for the comment.
 * @param {Object} [commentData.conversationFields] - Extra fields to merge into the conversation
 *   update, for callers that need their own conversation state written in the same atomic step as
 *   the comment. Applied first, so the archive state and message preview computed here always win:
 *   this function's contract is that it derives `lastMessageText` from `message`.
 * @param {Object} externalTransaction - The external transaction to use.
 * @returns {Promise<void>} Resolves with a boolean indicating whether the operation was successful.
 */
const addLeadComment = async ({
  leadId,
  coachId,
  createdBy,
  createdByUserName,
  message,
  shouldArchiveChat = false,
  conversationFields = {},
}, externalTransaction = null) => {
  const comment = {
    leadId,
    coachId,
    activityType: LeadHistoryType.COMMENT,
    value: message,
    createdAt: getFirebase().firestore.FieldValue.serverTimestamp(),
    createdBy,
    createdByUserName,
  };

  const db = getFirebase().default.firestore();
  const fieldValue = getFirebase().firestore.FieldValue;

  const upsertLeadComment = async (transaction) => {
    const conversationRef = db.collection(CollectionName.LEAD_CONVERSATION).doc(leadId);
    const conversationDoc = await transaction.get(conversationRef);
    if (!conversationDoc.exists) {
      throw new Error(`Lead conversation not found for lead ${leadId}`);
    }

    // create the comment for the lead
    const historyDocRef = db.collection(CollectionName.LEAD_HISTORY).doc();
    transaction.set(historyDocRef, comment);

    // upsert the lead's conversation by unarchiving it and updating the preview fields
    transaction.update(conversationRef, {
      ...conversationFields,
      isArchived: shouldArchiveChat,
      archivedAt: shouldArchiveChat ? getFirebase().firestore.FieldValue.serverTimestamp() : fieldValue.delete(),
      archivedBy: shouldArchiveChat ? createdBy : fieldValue.delete(),
      ...shouldArchiveChat ? {
        unarchivedBy: null,
        unarchivedAt: null,
      } : {
        lastMessageText: message,
        lastMessageAt: getFirebase().firestore.FieldValue.serverTimestamp(),
        lastMessageIsComment: true,
      },
    });
  };

  if (externalTransaction) {
    await upsertLeadComment(externalTransaction);
    return;
  }

  await db.runTransaction(async (transaction) => {
    await upsertLeadComment(transaction);
  });
};

/**
 * Return a previously-escalated lead back to aiSDR: re-enable automated SDR, clear the
 * escalation reason, and log a comment. No-op if the lead was never escalated from aiSDR,
 * so it can be called unconditionally from the send flow.
 *
 * @param {Object} lead - The Lead model instance to hand back.
 * @param {Object} opts
 * @param {string} opts.createdBy - User ID of the SDR performing the hand-back.
 * @param {string} opts.createdByUserName - Display name of the SDR performing the hand-back.
 * @returns {Promise<void>}
 */
const returnLeadToAiSdr = async (lead, { createdBy = '', createdByUserName = '' } = {}) => {
  if (!lead?.isEscalatedFromAiSdr) {
    return;
  }

  const db = getFirebase().default.firestore();
  const fieldValue = getFirebase().firestore.FieldValue;
  const leadRef = db.collection(CollectionName.LEADS).doc(lead.id);

  // Re-enable aiSDR and log the audit comment in a single transaction so the lead can never end
  // up back on aiSDR without the corresponding comment (or vice versa).
  await db.runTransaction(async (transaction) => {
    await addLeadComment({
      leadId: lead.id,
      coachId: lead.coach,
      createdBy,
      createdByUserName,
      message: AI_SDR_RE_ENABLED_COMMENT,
      shouldArchiveChat: true,
      conversationFields: { useAutomatedSdr: true },
    }, transaction);

    transaction.update(leadRef, {
      useAutomatedSdr: true,
      escalationReason: fieldValue.delete(),
    });
  });
};

/**
 * Create userActionItem documents for each mentioned user in a comment.
 *
 * @param {Object} params
 * @param {string[]} params.mentionedUserIds - IDs of users mentioned in the comment.
 * @param {string} params.leadId - ID of the lead.
 * @param {string} params.leadName - Name of the lead.
 * @param {string} params.triggeredBy - ID of the user who triggered the action item.
 * @param {string} params.triggerUserName - Display name of the user who wrote the comment.
 * @param {string} params.message - Raw comment text.
 * @returns {Promise<void>}
 */
const createLeadCommentActionItems = async ({
  mentionedUserIds,
  leadId,
  leadName,
  triggeredBy,
  triggerUserName,
  message,
}) => {
  if (!mentionedUserIds.length) return;

  const db = getFirebase().default.firestore();

  await Promise.all(
    mentionedUserIds.map((userId) => db.collection(CollectionName.USER_ACTION_ITEMS).doc().set({
      type: ActionItemType.LEAD_COMMENT,
      status: ActionItemStatus.PENDING,
      leadId,
      leadName,
      triggeredBy,
      triggerUserName,
      message,
      userId,
      createdAt: getFirebase().firestore.FieldValue.serverTimestamp(),
    })),
  );
};

/**
 * Update an existing comment in the lead history.
 *
 * @param {Object} params
 * @param {string} params.commentId - Firestore document ID of the comment.
 * @param {string} params.message - Updated comment text.
 * @param {string} [params.leadId] - ID of the lead.
 * @returns {Promise<void>}
 */
const updateLeadComment = async ({ commentId, message, leadId = null }) => {
  const db = getFirebase().default.firestore();
  await db.collection(CollectionName.LEAD_HISTORY).doc(commentId).update({
    value: message,
    updatedAt: getFirebase().firestore.FieldValue.serverTimestamp(),
  });
  if (leadId) {
    await db.collection(CollectionName.LEAD_CONVERSATION).doc(leadId).update({
      lastMessageText: message,
    });
  }
};

export {
  sendLeadMessage,
  addLeadComment,
  returnLeadToAiSdr,
  createLeadCommentActionItems,
  updateLeadComment,
};
